// Small fetch wrapper that retries transient backend blips with exponential
// backoff. A brief backend restart/deploy/failover surfaces as a 429 (rate
// limited) or a transient 5xx (502/503/504) from the proxy; retrying a few
// times with backoff lets desk loading ride out the blip instead of throwing a
// hard "Failed to load ..." error. Non-transient statuses (incl. 401) and the
// final attempt are returned to the caller as-is so it keeps full control over
// auth short-circuiting and error handling.

/** Statuses worth retrying: rate limiting and transient gateway/proxy errors. */
export function isRetryableStatus(status: number): boolean {
  return status === 429 || status === 502 || status === 503 || status === 504;
}

export interface FetchWithRetryOptions {
  /** Number of retries after the initial attempt. Default 3 (4 attempts total). */
  maxRetries?: number;
  /** Base backoff in ms; delay for attempt n is baseDelayMs * 2**n. Default 1000. */
  baseDelayMs?: number;
}

export async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  options: FetchWithRetryOptions = {},
): Promise<Response> {
  const { maxRetries = 3, baseDelayMs = 1000 } = options;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const response = await fetch(input, init);
    if (isRetryableStatus(response.status) && attempt < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * 2 ** attempt));
      continue;
    }
    return response;
  }
  // Unreachable: the loop always returns on the final attempt.
  throw new Error("fetchWithRetry: retries exhausted");
}
